import { c as _c } from "react/compiler-runtime";
import { Drawer } from '@base-ui/react/drawer';
import { Avatar, Button } from '@systemeio/components';
import { AnnotationQuestionIcon, LogOut01Icon, Settings02Icon, Upload03Icon } from '@systemeio/icons';
import { LocaleEnum } from '@systemeio/types';
import React, { useEffect, useRef } from 'react';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { AnimatedMenuIcon } from 'shared/components/navbar/components/animated-menu-icon';
import { LanguageMenu } from 'shared/components/navbar/components/language-menu';
import { MobileMenuItem } from 'shared/components/navbar/components/mobile-menu-item';
import { NavbarDrawer } from 'shared/components/navbar/components/navbar-drawer';
import { SubaccountSelector } from 'shared/components/navbar/components/subaccount-selector';
import { WorkspaceSelector } from 'shared/components/navbar/components/workspace-selector';
import { MOBILE_NAVBAR_ITEM_CLASS_NAME, NAVBAR_ICON_BUTTON_CLASS_NAME } from 'shared/components/navbar/constants';
import { useMenuItems } from 'shared/components/navbar/hooks/use-menu-items';
import { getHelpPageUrl } from 'shared/components/navbar/utils/get-help-page-url';
import { pageRoute } from 'shared/constants/routes.constants';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useRouter } from 'shared/hooks/use-router';
import useUser from 'shared/hooks/use-user';
import { getAccountAvatarName } from 'shared/hooks/use-user/utils/get-account-avatar-name';
import { isSIO } from 'shared/utils/is-sio';
import { twJoin, twMerge } from 'tailwind-merge';
export function MobileMenu() {
  const $ = _c(55);
  const {
    avatarUrl,
    dashboardUser,
    isDisabled,
    isLoading,
    isSupportImpersonation,
    isWorkspaceOwner,
    user
  } = useUser();
  const {
    menuItems,
    isReady
  } = useMenuItems();
  const {
    pathname
  } = useRouter();
  const {
    t
  } = useLocoTranslation();
  const drawerActionsRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      drawerActionsRef.current?.close();
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== pathname) {
    t1 = [pathname];
    $[1] = pathname;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = twJoin(NAVBAR_ICON_BUTTON_CLASS_NAME, "group");
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = isLoading || !isReady;
  let t4;
  if ($[4] !== t3) {
    t4 = <Drawer.Trigger aria-label="Toggle Menu" className={t2} data-test-id="mobile-menu-button" disabled={t3} render={_temp} />;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== avatarUrl || $[7] !== dashboardUser) {
    t5 = dashboardUser && <div className="flex min-w-0 items-center gap-3"><Avatar alt={`${dashboardUser.firstName ?? ""} ${dashboardUser.lastName ?? ""}`.trim()} size="lg" data-test-id="top-menu-avatar" {...getAccountAvatarName(dashboardUser)} src={avatarUrl} /><span className="min-w-0 truncate text-base font-semibold">{dashboardUser.firstName} {dashboardUser.lastName}</span></div>;
    $[6] = avatarUrl;
    $[7] = dashboardUser;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== isDisabled || $[10] !== isWorkspaceOwner || $[11] !== t) {
    t6 = !isDisabled && <Button aria-label={t("global.settings")} data-test-id="mobile-settings-button" variant="ghost-gray" size="icon-md" render={<LinkWithoutPrefetch href={isWorkspaceOwner ? "/profile/user-settings" : "/profile/account-settings"} />}><Settings02Icon /></Button>;
    $[9] = isDisabled;
    $[10] = isWorkspaceOwner;
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <div className="bg-bg-secondary border-border-100 m-4 mb-0 flex min-w-0 shrink-0 items-center justify-between gap-3 rounded-lg border px-3 py-2">{t5}{t6}</div>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== isDisabled || $[17] !== menuItems || $[18] !== user) {
    t8 = user && !isDisabled && menuItems.map(_temp2);
    $[16] = isDisabled;
    $[17] = menuItems;
    $[18] = user;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t8) {
    t9 = <div className="p-4"><div className="flex min-w-0 flex-col gap-1.5">{t8}</div></div>;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t || $[23] !== user) {
    t10 = isSIO() && <a aria-label={t("header.top_menu.help")} className={MOBILE_NAVBAR_ITEM_CLASS_NAME} data-test-id="help-button" target="_blank" rel="noreferrer" href={getHelpPageUrl(user?.dashboardLocale ? user.dashboardLocale : LocaleEnum.ENGLISH)}><AnnotationQuestionIcon /><span className="min-w-0 truncate">{t("header.top_menu.help")}</span></a>;
    $[22] = t;
    $[23] = user;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== isSupportImpersonation || $[26] !== isWorkspaceOwner || $[27] !== pathname || $[28] !== t || $[29] !== user?.userPlanId) {
    t11 = user?.userPlanId === 40 && isWorkspaceOwner && !isSupportImpersonation && <LinkWithoutPrefetch aria-current={pathname.includes(pageRoute.ProfileUserPlan) ? "page" : undefined} className={twMerge(MOBILE_NAVBAR_ITEM_CLASS_NAME, pathname.includes(pageRoute.ProfileUserPlan) && "text-text-link [&_svg]:text-icon-accent")} data-test-id="upgrade-plan-button" href={pageRoute.ProfileUserPlan}><Upload03Icon /><span className="min-w-0 truncate">{t("header.top_menu.upgrade")}</span></LinkWithoutPrefetch>;
    $[25] = isSupportImpersonation;
    $[26] = isWorkspaceOwner;
    $[27] = pathname;
    $[28] = t;
    $[29] = user?.userPlanId;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  let t13;
  let t14;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <LanguageMenu isDesktop={false} />;
    t13 = <SubaccountSelector isDesktop={false} />;
    t14 = <WorkspaceSelector isDesktop={false} />;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
  } else {
    t12 = $[31];
    t13 = $[32];
    t14 = $[33];
  }
  let t15;
  if ($[34] !== isSupportImpersonation || $[35] !== t) {
    t15 = isSupportImpersonation && <a href="/dashboard/back/entry?pretend_this_user=_exit" className={twMerge(MOBILE_NAVBAR_ITEM_CLASS_NAME, "text-text-error [&_svg]:text-icon-error")} data-test-id="exit-impersonation-button"><LogOut01Icon /><span className="min-w-0 truncate">{t("header.exit_impersonation")}</span></a>;
    $[34] = isSupportImpersonation;
    $[35] = t;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = twMerge(MOBILE_NAVBAR_ITEM_CLASS_NAME, "text-text-error [&_svg]:text-icon-error");
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <LogOut01Icon />;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t) {
    t18 = t("header.user.profile.menu.logout");
    $[39] = t;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t18) {
    t19 = <a href="/dashboard/logout" className={t16} data-test-id="logout-button">{t17}<span className="min-w-0 truncate">{t18}</span></a>;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t10 || $[44] !== t11 || $[45] !== t15 || $[46] !== t19) {
    t20 = <div className="border-border-100 mt-auto flex shrink-0 flex-col gap-2 border-t p-4">{t10}{t11}{t12}{t13}{t14}{t15}{t19}</div>;
    $[43] = t10;
    $[44] = t11;
    $[45] = t15;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t20 || $[49] !== t7 || $[50] !== t9) {
    t21 = <NavbarDrawer actionsRef={drawerActionsRef} className="border-t-0"><div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">{t7}{t9}{t20}</div></NavbarDrawer>;
    $[48] = t20;
    $[49] = t7;
    $[50] = t9;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== t21 || $[53] !== t4) {
    t22 = <Drawer.Root actionsRef={drawerActionsRef} disablePointerDismissal={true} modal={false} swipeDirection="right">{t4}{t21}</Drawer.Root>;
    $[52] = t21;
    $[53] = t4;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  return t22;
}
function _temp2(item) {
  return <MobileMenuItem item={item} key={"href" in item ? item.href : item.id} />;
}
function _temp(renderProps, state) {
  return <button {...renderProps}><AnimatedMenuIcon open={state.open} /></button>;
}